import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Store, Plus, Edit2, Trash2, Save, X, ExternalLink, Sparkles, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

function AdminRetailers() {
  const [retailers, setRetailers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    affiliate_link: '',
    display_order: 0,
    is_active: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchRetailers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRetailers = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/retailers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRetailers(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      name: '',
      description: '',
      affiliate_link: '',
      display_order: retailers.length + 1,
      is_active: true
    });
  };

  const handleEdit = (retailer) => {
    setEditingId(retailer.id);
    setFormData({
      name: retailer.name,
      description: retailer.description,
      affiliate_link: retailer.affiliate_link,
      display_order: retailer.display_order,
      is_active: retailer.is_active
    });
  };

  const handleSave = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      if (isAdding) {
        await axios.post(`${API}/admin/retailers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`${API}/admin/retailers/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchRetailers();
      setIsAdding(false);
      setEditingId(null);
    } catch (error) {
      alert('Error saving retailer');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this retailer?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/retailers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRetailers();
    } catch (error) {
      alert('Error deleting retailer');
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#D4AF37] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-retailers-page">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-2">
                <Store className="w-6 h-6 text-[#D4AF37]" />
                <h1 className="text-2xl font-serif font-bold text-[#D4AF37]">Manage Retailers</h1>
              </div>
            </div>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-semibold hover:bg-[#C5A028] transition-all flex items-center space-x-2"
              data-testid="add-retailer-btn"
            >
              <Plus className="w-5 h-5" />
              <span>Add Retailer</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Form */}
        {isAdding && (
          <div className="mb-6 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6" data-testid="add-retailer-form">
            <h3 className="text-xl font-bold text-[#D4AF37] mb-4">Add New Retailer</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="retailer-name-input"
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="retailer-description-input"
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Affiliate Link</label>
                <input
                  type="url"
                  value={formData.affiliate_link}
                  onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="https://..."
                  data-testid="retailer-link-input"
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="retailer-order-input"
                />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="mr-2"
                data-testid="retailer-active-checkbox"
              />
              <label className="text-gray-300">Active</label>
            </div>
            <div className="flex space-x-4 mt-6">
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-semibold hover:bg-[#C5A028] transition-all flex items-center space-x-2"
                data-testid="save-retailer-btn"
              >
                <Save className="w-5 h-5" />
                <span>Save</span>
              </button>
              <button
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-all flex items-center space-x-2"
                data-testid="cancel-btn"
              >
                <X className="w-5 h-5" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}

        {/* Retailers List */}
        <div className="space-y-4">
          {retailers.map((retailer, index) => (
            <div
              key={retailer.id}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6"
              data-testid={`retailer-card-${index}`}
            >
              {editingId === retailer.id ? (
                // Edit Mode
                <div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 mb-2">Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Description</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Affiliate Link</label>
                      <input
                        type="url"
                        value={formData.affiliate_link}
                        onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
                        className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Display Order</label>
                      <input
                        type="number"
                        value={formData.display_order}
                        onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>
                  <div className="flex items-center mt-4">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="mr-2"
                    />
                    <label className="text-gray-300">Active</label>
                  </div>
                  <div className="flex space-x-4 mt-6">
                    <button
                      onClick={handleSave}
                      className="px-6 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-semibold hover:bg-[#C5A028] transition-all flex items-center space-x-2"
                    >
                      <Save className="w-5 h-5" />
                      <span>Save</span>
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-all flex items-center space-x-2"
                    >
                      <X className="w-5 h-5" />
                      <span>Cancel</span>
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-xl font-bold text-[#D4AF37]">{retailer.name}</h3>
                      {!retailer.is_active && (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">Inactive</span>
                      )}
                    </div>
                    <p className="text-gray-400 mb-2">{retailer.description}</p>
                    <a
                      href={retailer.affiliate_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#D4AF37] hover:underline flex items-center space-x-1"
                    >
                      <span>{retailer.affiliate_link}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <p className="text-sm text-gray-500 mt-2">Display Order: {retailer.display_order}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(retailer)}
                      className="p-2 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 border border-[#D4AF37]/30 rounded-lg transition-all"
                      data-testid={`edit-retailer-btn-${index}`}
                    >
                      <Edit2 className="w-5 h-5 text-[#D4AF37]" />
                    </button>
                    <button
                      onClick={() => handleDelete(retailer.id)}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-all"
                      data-testid={`delete-retailer-btn-${index}`}
                    >
                      <Trash2 className="w-5 h-5 text-red-400" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {retailers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Store className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No retailers yet. Click "Add Retailer" to get started.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminRetailers;